import { useState, useEffect, useRef } from 'react';
import { SidebarProvider } from "@/components/ui/sidebar";
import OwnerSidebar from "./OwnerSidebar";
import OwnerHeader from "./OwnerHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AddWebsiteDataForm from "./AddWebsiteDataForm";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { db, storage } from "@/firebase/config";
import { collection, doc, getDoc, setDoc, getDocs, orderBy, query, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Globe, Plus, Loader2, CheckCircle2, XCircle, Search, ExternalLink, RefreshCw, Edit2, Palette, Settings, HardDrive, Shield, Zap } from "lucide-react";
import ContentLoader from '@/authentication/ContentLoader';

interface SchoolDoc {
  id: string;
  name: string;
  subdomain: string;
  logoUrl?: string;
  primaryColor: string;
  welcomeMessage?: string;
  isActive: boolean;
  createdAt?: any;
  hasWebsite?: boolean;
  selectedDesignId?: string;
    linkedSystemSchoolId?: string | null;
  staffMembers?: StaffMember[];
}

export interface StaffMember {
    id: string;
    title: string;
    name: string;
    description: string;
    rank: number;
    photoUrl?: string;
    photoFile?: File | null;
}

export interface BoardMember {
    id: string;
    title: string;
    name: string;
    role: string;
    photoUrl?: string;
    photoFile?: File | null;
}

export interface SchoolFacility {
    id: string;
    categoryId: string;
    title: string;
    description: string;
    photoUrl?: string;
    photoFile?: File | null;
}

export interface GalleryItem {
    id: string;
    categoryId: string;
    name: string;
    description?: string;
    photoUrl?: string;
    photoFile?: File | null;
}

export interface ActivityItem {
    id: string;
    title: string;
    description: string;
    photoUrl?: string;
    photo: File | null;
}

export interface PaymentMethod {
    id: string;
    method: string;
    details: string;
}

const OwnerSchoolWebsite = () => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(true);
    const [schools, setSchools] = useState<SchoolDoc[]>([]);
    const [view, setView] = useState<'list' | 'create' | 'create_custom' | 'edit' | 'setup'>('list');
    const [editingSchool, setEditingSchool] = useState<SchoolDoc | null>(null);

    // Basic Form State
    const [selectedSchoolId, setSelectedSchoolId] = useState<string | "new">("new");
    const [schoolName, setSchoolName] = useState("");
    const [subdomain, setSubdomain] = useState("");
    const [primaryColor, setPrimaryColor] = useState("#2563EB");
    const [welcomeMessage, setWelcomeMessage] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [motto, setMotto] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Contact & Location
    const [phones, setPhones] = useState<string[]>([""]);
    const [email, setEmail] = useState("");
    const [whatsapp, setWhatsapp] = useState("");
    const [address, setAddress] = useState("");
    const [mapsLink, setMapsLink] = useState("");
    const [fbLink, setFbLink] = useState("");
    const [twitterLink, setTwitterLink] = useState("");
    const [instaLink, setInstaLink] = useState("");

    // Story sections
    const [mission, setMission] = useState("");
    const [vision, setVision] = useState("");
    const [about, setAbout] = useState("");
    const [nationalAnthem, setNationalAnthem] = useState("");
    const [schoolAnthem, setSchoolAnthem] = useState("");
    const [whyChooseUs, setWhyChooseUs] = useState("");
    const [historyText, setHistoryText] = useState("");
    const [coreValues, setCoreValues] = useState<string[]>([""]);
    const [quote, setQuote] = useState("");
    const [quoteAuthor, setQuoteAuthor] = useState("");

    // Story section image files
    const [missionFile, setMissionFile] = useState<File | null>(null);
    const [visionFile, setVisionFile] = useState<File | null>(null);
    const [aboutFile, setAboutFile] = useState<File | null>(null);
    const [nationalAnthemFile, setNationalAnthemFile] = useState<File | null>(null);
    const [schoolAnthemFile, setSchoolAnthemFile] = useState<File | null>(null);
    const [whyChooseUsFile, setWhyChooseUsFile] = useState<File | null>(null);

    // Header photos
    const [headerPhotos, setHeaderPhotos] = useState<(File | null)[]>([null, null, null]);

    // Statistics
    const [totalStudents, setTotalStudents] = useState("");
    const [totalStaff, setTotalStaff] = useState("");
    const [yearFounded, setYearFounded] = useState("");
    const [totalAlumni, setTotalAlumni] = useState("");

    // Principal
    const [principalName, setPrincipalName] = useState("");
    const [principalPhoto, setPrincipalPhoto] = useState<File | null>(null);
    const [principalMessage, setPrincipalMessage] = useState("");

    // Curriculum
    const [curriculumDesc, setCurriculumDesc] = useState("");
    const [compulsorySubjects, setCompulsorySubjects] = useState<string[]>([""]);
    const [optionalSubjects, setOptionalSubjects] = useState<string[]>([""]);

    // Arrays
    const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
    const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
    const [activities, setActivities] = useState<ActivityItem[]>([]);
    const [facilities, setFacilities] = useState<SchoolFacility[]>([]);
    const [facilityCategories, setFacilityCategories] = useState<string[]>(["Academic", "Sports", "Technology", "Boarding"]);
    const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
    const [galleryCategories, setGalleryCategories] = useState<string[]>(["Events", "Infrastructure", "Students", "Trips"]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [feeBreakdown, setFeeBreakdown] = useState("");

    // Admissions
    const [admissionProcess, setAdmissionProcess] = useState("");
    const [admissionRequirements, setAdmissionRequirements] = useState<string[]>([""]);
    const [admissionFormFile, setAdmissionFormFile] = useState<File | null>(null);

    // Validation State
    const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
    const [validatingSlug, setValidatingSlug] = useState(false);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        loadSchools();
    }, []);

    const loadSchools = async () => {
        setLoading(true);
        try {
            // First, load all published websites from schoolWebsites collection
            const websitesCol = collection(db, "schoolWebsites");
            const websitesSnapshot = await getDocs(websitesCol);
            
            const websitesMap = new Map<string, any>();
            websitesSnapshot.docs.forEach((websiteDoc) => {
                const data = websiteDoc.data();
                websitesMap.set(websiteDoc.id, {
                    id: websiteDoc.id,
                    name: data.name || "Unnamed School",
                    subdomain: websiteDoc.id, // doc ID is the subdomain
                    logoUrl: data.logoUrl || "",
                    primaryColor: data.primaryColor || "#2563EB",
                    welcomeMessage: data.welcomeMessage,
                    isActive: data.isActive ?? true,
                    createdAt: data.createdAt,
                    selectedDesignId: data.selectedDesignId,
                    linkedSystemSchoolId: data.linkedSystemSchoolId ?? null,
                    hasWebsite: true
                });
            });

            // Also load from schools collection for schools without websites
            const schoolsCol = collection(db, "schools");
            const schoolsSnapshot = await getDocs(schoolsCol);
            
            for (const schoolDoc of schoolsSnapshot.docs) {
                const schoolID = schoolDoc.id;
                
                // Check if this school already has a website (by checking if any website references this school)
                let hasWebsite = false;
                websitesMap.forEach((w) => {
                    if (w.linkedSystemSchoolId === schoolID) {
                        hasWebsite = true;
                    }
                });
                
                // If no website for this school, add it to the list
                if (!hasWebsite && !websitesMap.has(schoolID)) {
                    const rootData = schoolDoc.data();
                    const detailsDoc = await getDoc(doc(db, "schools", schoolID, "schoolDetails", "main"));
                    const detailsData = detailsDoc.exists() ? detailsDoc.data() : {};
                    
                    websitesMap.set(schoolID, {
                        id: schoolID,
                        name: rootData.name || detailsData.name || "Unnamed School",
                        subdomain: "", // No website set
                        logoUrl: rootData.logoUrl || "",
                        primaryColor: rootData.primaryColor || "#2563EB",
                        welcomeMessage: rootData.welcomeMessage,
                        isActive: rootData.isActive ?? true,
                        createdAt: rootData.createdAt,
                        hasWebsite: false
                    });
                }
            }

            // Convert map to array and sort
            const allSchools = Array.from(websitesMap.values()) as SchoolDoc[];
            allSchools.sort((a, b) => a.name.localeCompare(b.name));
            setSchools(allSchools);

        } catch (error) {
            console.error("Error loading schools", error);
            toast({ title: "Error", description: "Failed to load schools.", variant: "destructive" });
        } finally {
            setLoading(false);
        }
    };

    // Handle editing an existing school website
    const handleEditSchool = (school: SchoolDoc) => {
        setEditingSchool(school);
        setSelectedSchoolId(school.id);
        setSchoolName(school.name);
        setSubdomain(school.subdomain);
        setPrimaryColor(school.primaryColor || "#2563EB");
        setWelcomeMessage(school.welcomeMessage || "");
        setIsActive(school.isActive);
        setSlugAvailable(true); // Existing subdomain is valid
        setView('edit');
    };

    // Open design preview for a school
    const handleChangeDesign = async (school: SchoolDoc) => {
        try {
            // Fetch the FULL document to get all content (hero, gallery, etc)
            const docRef = doc(db, "schoolWebsites", school.subdomain);
            const docSnap = await getDoc(docRef);
            
            let fullData = {};
            if (docSnap.exists()) {
                fullData = docSnap.data();
            } else {
                // Fallback to basic info if doc missing
                fullData = {
                    name: school.name,
                    subdomain: school.subdomain,
                    primaryColor: school.primaryColor,
                    welcomeMessage: school.welcomeMessage,
                    logoUrl: school.logoUrl,
                    selectedDesignId: school.selectedDesignId
                };
            }

            localStorage.setItem('previewSchoolData', JSON.stringify(fullData));
            window.open('/owner/school-website-preview', '_blank');
        } catch (error) {
            console.error("Error fetching school data for preview:", error);
            toast({ title: "Error", description: "Failed to load website data.", variant: "destructive" });
        }
    };

    // Handle selecting an existing school to make a website for
    const handleSchoolSelection = (value: string) => {
        setSelectedSchoolId(value);
        if (value === "new") {
            setSchoolName("");
            setSubdomain("");
            return;
        }

        const selected = schools.find(s => s.id === value);
        if (selected) {
            setSchoolName(selected.name);
            // Trigger slug generation manually since we aren't typing
            const slug = selected.name.toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            setSubdomain(slug);
            setSlugAvailable(null);
        }
    };

    // Auto-generate slug
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setSchoolName(name);
        
        if (selectedSchoolId === "new") {
             const slug = name.toLowerCase()
                .replace(/[^a-z0-9]+/g, '-') 
                .replace(/^-+|-+$/g, '');   
            setSubdomain(slug);
            setSlugAvailable(null); 
        }
    };

    const checkAvailability = async () => {
        if (!subdomain) return;
        setValidatingSlug(true);
        try {
            const websiteDocRef = doc(db, "schoolWebsites", subdomain);
            const websiteDocSnap = await getDoc(websiteDocRef);

            if (editingSchool && editingSchool.subdomain === subdomain) {
                setSlugAvailable(true);
                return;
            }

            setSlugAvailable(!websiteDocSnap.exists());
        } catch (error) {
            console.error(error);
        } finally {
            setValidatingSlug(false);
        }
    };
    
    useEffect(() => {
        const timer = setTimeout(() => {
            if (subdomain.length > 2) checkAvailability();
        }, 800);
        return () => clearTimeout(timer);
    }, [subdomain]);

    const resetForm = () => {
        setSchoolName(""); setSubdomain(""); setWelcomeMessage(""); setPrimaryColor("#2563EB");
        setLogoFile(null); setSelectedSchoolId("new"); setMotto("");
        setPhones([""]); setEmail(""); setWhatsapp(""); setAddress(""); setMapsLink("");
        setFbLink(""); setTwitterLink(""); setInstaLink("");
        setMission(""); setVision(""); setAbout(""); setNationalAnthem(""); setSchoolAnthem("");
        setWhyChooseUs(""); setHistoryText(""); setCoreValues([""]); setQuote(""); setQuoteAuthor("");
        setMissionFile(null); setVisionFile(null); setAboutFile(null);
        setNationalAnthemFile(null); setSchoolAnthemFile(null); setWhyChooseUsFile(null);
        setHeaderPhotos([null, null, null]);
        setTotalStudents(""); setTotalStaff(""); setYearFounded(""); setTotalAlumni("");
        setPrincipalName(""); setPrincipalPhoto(null); setPrincipalMessage("");
        setCurriculumDesc(""); setCompulsorySubjects([""]); setOptionalSubjects([""]);
        setStaffMembers([]); setBoardMembers([]); setActivities([]);
        setFacilities([]); setFacilityCategories(["Academic", "Sports", "Technology", "Boarding"]);
        setGalleryItems([]); setGalleryCategories(["Events", "Infrastructure", "Students", "Trips"]);
        setPaymentMethods([]); setFeeBreakdown("");
        setAdmissionProcess(""); setAdmissionRequirements([""]); setAdmissionFormFile(null);
    };

    const handleCreate = async () => {
        if (!slugAvailable) {
            toast({ title: "Error", description: "Subdomain is not available.", variant: "destructive"});
            return;
        }
        if (!schoolName) {
            toast({ title: "Error", description: "School Name is required.", variant: "destructive"});
            return;
        }

        setCreating(true);
        try {
            const base = `schoolWebsites/${subdomain}`;

            // --- Upload logo ---
            let logoUrl = "";
            if (logoFile) {
                const storageRef = ref(storage, `${base}/logo.png`);
                await uploadBytes(storageRef, logoFile);
                logoUrl = await getDownloadURL(storageRef);
            }

            // --- Upload header photos ---
            const headerPhotoUrls: string[] = [];
            for (let i = 0; i < headerPhotos.length; i++) {
                if (headerPhotos[i]) {
                    const storageRef = ref(storage, `${base}/header_${i}.jpg`);
                    await uploadBytes(storageRef, headerPhotos[i]!);
                    headerPhotoUrls.push(await getDownloadURL(storageRef));
                }
            }

            // --- Upload principal photo ---
            let principalPhotoUrl = "";
            if (principalPhoto) {
                const storageRef = ref(storage, `${base}/principal.jpg`);
                await uploadBytes(storageRef, principalPhoto);
                principalPhotoUrl = await getDownloadURL(storageRef);
            }

            // --- Upload story section images ---
            const uploadStory = async (file: File | null, name: string) => {
                if (!file) return "";
                const storageRef = ref(storage, `${base}/story_${name}.jpg`);
                await uploadBytes(storageRef, file);
                return getDownloadURL(storageRef);
            };
            const [missionImgUrl, visionImgUrl, aboutImgUrl, nationalAnthemImgUrl, schoolAnthemImgUrl, whyChooseUsImgUrl] = await Promise.all([
                uploadStory(missionFile, "mission"),
                uploadStory(visionFile, "vision"),
                uploadStory(aboutFile, "about"),
                uploadStory(nationalAnthemFile, "nationalAnthem"),
                uploadStory(schoolAnthemFile, "schoolAnthem"),
                uploadStory(whyChooseUsFile, "whyChooseUs"),
            ]);

            // --- Upload staff photos ---
            const staffData = await Promise.all(staffMembers.map(async (s) => {
                let photoUrl = s.photoUrl || "";
                if (s.photoFile) {
                    const storageRef = ref(storage, `${base}/staff_${s.id}.jpg`);
                    await uploadBytes(storageRef, s.photoFile);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: s.id, title: s.title, name: s.name, description: s.description, rank: s.rank, photoUrl };
            }));

            // --- Upload board member photos ---
            const boardData = await Promise.all(boardMembers.map(async (b) => {
                let photoUrl = b.photoUrl || "";
                if (b.photoFile) {
                    const storageRef = ref(storage, `${base}/board_${b.id}.jpg`);
                    await uploadBytes(storageRef, b.photoFile);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: b.id, title: b.title, name: b.name, role: b.role, photoUrl };
            }));

            // --- Upload activity photos ---
            const activitiesData = await Promise.all(activities.map(async (a) => {
                let photoUrl = a.photoUrl || "";
                if (a.photo) {
                    const storageRef = ref(storage, `${base}/activity_${a.id}.jpg`);
                    await uploadBytes(storageRef, a.photo);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: a.id, title: a.title, description: a.description, photoUrl };
            }));

            // --- Upload facility photos ---
            const facilitiesData = await Promise.all(facilities.map(async (f) => {
                let photoUrl = f.photoUrl || "";
                if (f.photoFile) {
                    const storageRef = ref(storage, `${base}/facility_${f.id}.jpg`);
                    await uploadBytes(storageRef, f.photoFile);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: f.id, categoryId: f.categoryId, title: f.title, description: f.description, photoUrl };
            }));

            // --- Upload gallery photos ---
            const galleryData = await Promise.all(galleryItems.map(async (g) => {
                let photoUrl = g.photoUrl || "";
                if (g.photoFile) {
                    const storageRef = ref(storage, `${base}/gallery_${g.id}.jpg`);
                    await uploadBytes(storageRef, g.photoFile);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: g.id, categoryId: g.categoryId, name: g.name, description: g.description || "", photoUrl };
            }));

            // --- Upload admission form ---
            let admissionFormUrl = "";
            if (admissionFormFile) {
                const storageRef = ref(storage, `${base}/admission_form.pdf`);
                await uploadBytes(storageRef, admissionFormFile);
                admissionFormUrl = await getDownloadURL(storageRef);
            }

            // --- Build the full document ---
            const schoolData = {
                subdomain,
                name: schoolName,
                motto,
                primaryColor,
                welcomeMessage,
                isActive,
                logoUrl,
                linkedSystemSchoolId: selectedSchoolId === "new" ? null : selectedSchoolId,
                createdAt: serverTimestamp(),

                // Theme: store colors in both nested and flat format so all design components can read them
                theme: {
                    colors: {
                        primary: primaryColor || '#1e3a5f',
                        secondary: '#228b22',
                        accent: '#ffd700',
                        background: '#ffffff',
                        text: '#1e293b',
                        textMuted: '#64748b',
                    }
                },
                colors: {
                    primary: primaryColor || '#1e3a5f',
                    secondary: '#228b22',
                    accent: '#ffd700',
                },

                // Header
                headerPhotoUrls,

                // Statistics
                stats: { totalStudents, totalStaff, yearFounded, totalAlumni },

                // Principal
                principal: { name: principalName, photoUrl: principalPhotoUrl, message: principalMessage },

                // Story sections
                mission: { text: mission, imageUrl: missionImgUrl },
                vision: { text: vision, imageUrl: visionImgUrl },
                about: { text: about, imageUrl: aboutImgUrl },
                history: historyText,
                nationalAnthem: { text: nationalAnthem, imageUrl: nationalAnthemImgUrl },
                schoolAnthem: { text: schoolAnthem, imageUrl: schoolAnthemImgUrl },
                whyChooseUs: { text: whyChooseUs, imageUrl: whyChooseUsImgUrl },
                coreValues,
                quote: { text: quote, author: quoteAuthor },

                // People
                staffMembers: staffData,
                boardMembers: boardData,

                // Curriculum
                curriculum: { description: curriculumDesc, compulsorySubjects, optionalSubjects },

                // Activities & Facilities & Gallery
                activities: activitiesData,
                facilityCategories,
                facilities: facilitiesData,
                galleryCategories,
                gallery: galleryData,

                // Contact
                contact: {
                    phones: phones.filter(Boolean),
                    email,
                    whatsapp,
                    address,
                    mapsLink,
                    social: { facebook: fbLink, twitter: twitterLink, instagram: instaLink },
                },

                // Admissions
                admissions: {
                    process: admissionProcess,
                    requirements: admissionRequirements.filter(Boolean),
                    formUrl: admissionFormUrl,
                },

                // Fees
                fees: {
                    paymentMethods: paymentMethods.map(({ id, method, details }) => ({ id, method, details })),
                    breakdown: feeBreakdown,
                },

                isDeployed: false,
                newsItems: [],
                events: [],
            };

            await setDoc(doc(db, "schoolWebsites", subdomain), schoolData);

            toast({ title: "Success! Website Data Saved", description: `${schoolName} data has been saved successfully.` });
            resetForm();
            setView('list');
            loadSchools();
        } catch (error) {
            console.error("Error creating school", error);
            toast({ title: "Error", description: "Failed to create school website.", variant: "destructive"});
        } finally {
            setCreating(false);
        }
    };

    // Handle updating an existing school website
    const handleUpdate = async () => {
        if (!editingSchool) return;
        if (!schoolName) {
            toast({ title: "Error", description: "School Name is required.", variant: "destructive"});
            return;
        }

        const subdomainChanged = subdomain !== editingSchool.subdomain;
        if (subdomainChanged && !slugAvailable) {
            toast({ title: "Error", description: "New subdomain is not available.", variant: "destructive"});
            return;
        }

        setCreating(true);
        try {
            const base = `schoolWebsites/${subdomain}`;

            let logoUrl = editingSchool.logoUrl || "";
            if (logoFile) {
                const storageRef = ref(storage, `${base}/logo.png`);
                await uploadBytes(storageRef, logoFile);
                logoUrl = await getDownloadURL(storageRef);
            }

            const headerPhotoUrls: string[] = [];
            for (let i = 0; i < headerPhotos.length; i++) {
                if (headerPhotos[i]) {
                    const storageRef = ref(storage, `${base}/header_${i}.jpg`);
                    await uploadBytes(storageRef, headerPhotos[i]!);
                    headerPhotoUrls.push(await getDownloadURL(storageRef));
                }
            }

            let principalPhotoUrl = "";
            if (principalPhoto) {
                const storageRef = ref(storage, `${base}/principal.jpg`);
                await uploadBytes(storageRef, principalPhoto);
                principalPhotoUrl = await getDownloadURL(storageRef);
            }

            const uploadStory = async (file: File | null, name: string) => {
                if (!file) return "";
                const storageRef = ref(storage, `${base}/story_${name}.jpg`);
                await uploadBytes(storageRef, file);
                return getDownloadURL(storageRef);
            };
            const [missionImgUrl, visionImgUrl, aboutImgUrl, nationalAnthemImgUrl, schoolAnthemImgUrl, whyChooseUsImgUrl] = await Promise.all([
                uploadStory(missionFile, "mission"),
                uploadStory(visionFile, "vision"),
                uploadStory(aboutFile, "about"),
                uploadStory(nationalAnthemFile, "nationalAnthem"),
                uploadStory(schoolAnthemFile, "schoolAnthem"),
                uploadStory(whyChooseUsFile, "whyChooseUs"),
            ]);

            const staffData = await Promise.all(staffMembers.map(async (s) => {
                let photoUrl = s.photoUrl || "";
                if (s.photoFile) {
                    const storageRef = ref(storage, `${base}/staff_${s.id}.jpg`);
                    await uploadBytes(storageRef, s.photoFile);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: s.id, title: s.title, name: s.name, description: s.description, rank: s.rank, photoUrl };
            }));

            const boardData = await Promise.all(boardMembers.map(async (b) => {
                let photoUrl = b.photoUrl || "";
                if (b.photoFile) {
                    const storageRef = ref(storage, `${base}/board_${b.id}.jpg`);
                    await uploadBytes(storageRef, b.photoFile);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: b.id, title: b.title, name: b.name, role: b.role, photoUrl };
            }));

            const activitiesData = await Promise.all(activities.map(async (a) => {
                let photoUrl = a.photoUrl || "";
                if (a.photo) {
                    const storageRef = ref(storage, `${base}/activity_${a.id}.jpg`);
                    await uploadBytes(storageRef, a.photo);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: a.id, title: a.title, description: a.description, photoUrl };
            }));

            const facilitiesData = await Promise.all(facilities.map(async (f) => {
                let photoUrl = f.photoUrl || "";
                if (f.photoFile) {
                    const storageRef = ref(storage, `${base}/facility_${f.id}.jpg`);
                    await uploadBytes(storageRef, f.photoFile);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: f.id, categoryId: f.categoryId, title: f.title, description: f.description, photoUrl };
            }));

            const galleryData = await Promise.all(galleryItems.map(async (g) => {
                let photoUrl = g.photoUrl || "";
                if (g.photoFile) {
                    const storageRef = ref(storage, `${base}/gallery_${g.id}.jpg`);
                    await uploadBytes(storageRef, g.photoFile);
                    photoUrl = await getDownloadURL(storageRef);
                }
                return { id: g.id, categoryId: g.categoryId, name: g.name, description: g.description || "", photoUrl };
            }));

            let admissionFormUrl = "";
            if (admissionFormFile) {
                const storageRef = ref(storage, `${base}/admission_form.pdf`);
                await uploadBytes(storageRef, admissionFormFile);
                admissionFormUrl = await getDownloadURL(storageRef);
            }

            const updatedData: Record<string, any> = {
                subdomain,
                name: schoolName,
                motto,
                primaryColor,
                welcomeMessage,
                isActive,
                logoUrl,
                updatedAt: serverTimestamp(),
                // Sync theme colors on every update
                theme: {
                    colors: {
                        primary: primaryColor || '#1e3a5f',
                        secondary: '#228b22',
                        accent: '#ffd700',
                        background: '#ffffff',
                        text: '#1e293b',
                        textMuted: '#64748b',
                    }
                },
                colors: {
                    primary: primaryColor || '#1e3a5f',
                    secondary: '#228b22',
                    accent: '#ffd700',
                },
                headerPhotoUrls,
                stats: { totalStudents, totalStaff, yearFounded, totalAlumni },
                principal: { name: principalName, photoUrl: principalPhotoUrl, message: principalMessage },
                mission: { text: mission, imageUrl: missionImgUrl },
                vision: { text: vision, imageUrl: visionImgUrl },
                about: { text: about, imageUrl: aboutImgUrl },
                history: historyText,
                nationalAnthem: { text: nationalAnthem, imageUrl: nationalAnthemImgUrl },
                schoolAnthem: { text: schoolAnthem, imageUrl: schoolAnthemImgUrl },
                whyChooseUs: { text: whyChooseUs, imageUrl: whyChooseUsImgUrl },
                coreValues,
                quote: { text: quote, author: quoteAuthor },
                staffMembers: staffData,
                boardMembers: boardData,
                curriculum: { description: curriculumDesc, compulsorySubjects, optionalSubjects },
                activities: activitiesData,
                facilityCategories,
                facilities: facilitiesData,
                galleryCategories,
                gallery: galleryData,
                contact: {
                    phones: phones.filter(Boolean),
                    email,
                    whatsapp,
                    address,
                    mapsLink,
                    social: { facebook: fbLink, twitter: twitterLink, instagram: instaLink },
                },
                admissions: {
                    process: admissionProcess,
                    requirements: admissionRequirements.filter(Boolean),
                    formUrl: admissionFormUrl,
                },
                fees: {
                    paymentMethods: paymentMethods.map(({ id, method, details }) => ({ id, method, details })),
                    breakdown: feeBreakdown,
                },
            };

            if (subdomainChanged) {
                const oldDoc = await getDoc(doc(db, "schoolWebsites", editingSchool.subdomain));
                const oldData = oldDoc.exists() ? oldDoc.data() : {};
                await setDoc(doc(db, "schoolWebsites", subdomain), { ...oldData, ...updatedData });
                toast({ title: "Website Data Updated!", description: `Subdomain changed to ${subdomain}. Data saved successfully.` });
            } else {
                await setDoc(doc(db, "schoolWebsites", subdomain), updatedData, { merge: true });
                toast({ title: "Website Data Updated!", description: `Changes saved successfully for ${schoolName}.` });
            }

            setEditingSchool(null);
            resetForm();
            setView('list');
            loadSchools();
        } catch (error) {
            console.error("Error updating school", error);
            toast({ title: "Error", description: "Failed to update school website.", variant: "destructive"});
        } finally {
            setCreating(false);
        }
    };

    const handlePreview = () => {
        const buildPreviewData = (logoUrl: string) => ({
           subdomain: subdomain || "preview",
           name: schoolName || "School Name Preview",
           motto,
           primaryColor,
           welcomeMessage: welcomeMessage || "Welcome to our school website.",
           isActive,
           logoUrl,
           selectedDesignId: 'modern-elite',
           headerPhotos: headerPhotos.filter(Boolean),
           stats: {
                totalStudents,
                totalStaff,
                yearFounded,
                totalAlumni,
           },
           principal: {
                name: principalName,
                message: principalMessage,
           },
           mission: { text: mission },
           vision: { text: vision },
           about: { text: about },
           history: historyText,
           nationalAnthem: { text: nationalAnthem },
           schoolAnthem: { text: schoolAnthem },
           whyChooseUs: { text: whyChooseUs },
           coreValues: coreValues.filter(Boolean),
           quote: { text: quote, author: quoteAuthor },
           curriculum: {
                description: curriculumDesc,
                compulsorySubjects: compulsorySubjects.filter(Boolean),
                optionalSubjects: optionalSubjects.filter(Boolean),
           },
           staffMembers,
           boardMembers,
           activities,
           facilities,
           facilityCategories,
           gallery: galleryItems,
           galleryCategories,
           contact: {
                phones: phones.filter(Boolean),
                email,
                whatsapp,
                address,
                mapsLink,
                social: {
                    facebook: fbLink,
                    twitter: twitterLink,
                    instagram: instaLink,
                },
           },
           admissions: {
                process: admissionProcess,
                requirements: admissionRequirements.filter(Boolean),
           },
           fees: {
                paymentMethods: paymentMethods.map(({ id, method, details }) => ({ id, method, details })),
                breakdown: feeBreakdown,
           }
        });

        const openPreview = (dataWithLogo: any) => {
            localStorage.setItem('previewSchoolData', JSON.stringify(dataWithLogo));
            window.open('/owner/school-website-preview', '_blank');
        };

        if (logoFile) {
            const reader = new FileReader();
            reader.onloadend = () => {
                openPreview(buildPreviewData(String(reader.result || "")));
            };
            reader.readAsDataURL(logoFile);
        } else {
            const existingSchool = schools.find(s => s.id === selectedSchoolId);
            openPreview(buildPreviewData(existingSchool?.logoUrl || editingSchool?.logoUrl || ""));
        }
    };

    if (loading) {
         return (
             <SidebarProvider>
                 <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
                     <OwnerSidebar userRole="owner" activeSection="school-website" onSectionChange={() => {}} />
                     <div className="flex-1 flex flex-col items-center justify-center">
                         <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
                     </div>
                 </div>
             </SidebarProvider>
         )
    }

    return (
        <SidebarProvider>
            <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 text-slate-900 dark:text-slate-50">
                <OwnerSidebar userRole="owner" activeSection="school-website" onSectionChange={() => {}} />
                <div className="flex flex-col flex-1 h-full overflow-hidden">
                    <OwnerHeader />
                    <main className="flex-1 overflow-y-auto p-4 sm:p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight">School Websites</h1>
                                <p className="text-muted-foreground mt-1">Create and manage viral school homepages.</p>
                            </div>
                            {view === 'list' && (
                                <div className="flex gap-2">
                                    <Button onClick={() => setView('create')} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
                                        <Plus className="h-4 w-4" /> Add Website
                                    </Button>
                                    <Button onClick={() => { setView('create_custom'); setSelectedSchoolId("new"); }} className="gap-2 bg-green-600 hover:bg-green-700 text-white">
                                        <Plus className="h-4 w-4" /> Add Custom Website
                                    </Button>
                                </div>
                            )}
                            {(view === 'create' || view === 'create_custom') && (
                                <div className="flex gap-2">
                                     <Button onClick={handlePreview} variant="secondary" className="gap-2">
                                        <ExternalLink className="h-4 w-4" /> Preview
                                     </Button>
                                     <Button onClick={() => setView('list')} variant="ghost">Cancel</Button>
                                </div>
                            )}
                        </div>

                        {view === 'list' ? (
                            <Card>
                                <CardContent className="p-0">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>School Name</TableHead>
                                                <TableHead>Subdomain</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Live Link</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {schools.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} className="text-center h-32 text-muted-foreground">
                                                        No school websites created yet.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                schools.map(school => (
                                                    <TableRow key={school.id}>
                                                        <TableCell className="font-medium">
                                                            <div className="flex items-center gap-2">
                                                                {school.logoUrl && <img src={school.logoUrl} className="h-6 w-6 rounded-full object-contain bg-white" alt="" />}
                                                                {school.name}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {school.subdomain ? (
                                                                <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded dark:bg-slate-800">
                                                                    {school.subdomain}.sonoma.school
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted-foreground text-sm italic">Not Set</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {school.subdomain ? (
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${school.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}>
                                                                    {school.isActive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-muted-foreground">No Website</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {school.subdomain ? (
                                                                <a href={`https://${school.subdomain}.sonoma.school`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                                                                    Visit <ExternalLink className="h-3 w-3" />
                                                                </a>
                                                            ) : (
                                                                <Button variant="ghost" size="sm" onClick={() => {
                                                                    setSelectedSchoolId(school.id);
                                                                    setSchoolName(school.name);
                                                                    const slug = school.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                                                                    setSubdomain(slug);
                                                                    setView('create');
                                                                }} className="text-xs h-7">
                                                                    <Plus className="h-3 w-3 mr-1" /> Create
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {school.subdomain ? (
                                                                <div className="flex items-center gap-1">
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="sm" 
                                                                        onClick={() => handleEditSchool(school)} 
                                                                        className="text-xs h-7 px-2"
                                                                        title="Edit subdomain & settings"
                                                                    >
                                                                        <Edit2 className="h-3 w-3 mr-1" /> Edit
                                                                    </Button>
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="sm" 
                                                                        onClick={() => handleChangeDesign(school)} 
                                                                        className="text-xs h-7 px-2"
                                                                        title="Change website design"
                                                                    >
                                                                        <Palette className="h-3 w-3 mr-1" /> Design
                                                                    </Button>
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="sm" 
                                                                        onClick={() => { setEditingSchool(school); setView('setup'); }} 
                                                                        className="text-xs h-7 px-2"
                                                                        title="Setup school website"
                                                                    >
                                                                        <Settings className="h-3 w-3 mr-1" /> Setup
                                                                    </Button>
                                                                </div>
                                                            ) : (
                                                                <span className="text-xs text-muted-foreground">—</span>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        ) : (
                            <AddWebsiteDataForm 
                                view={view}
                                editingSchool={editingSchool}
                                schools={schools}
                                selectedSchoolId={selectedSchoolId}
                                schoolName={schoolName}
                                subdomain={subdomain}
                                isActive={isActive}
                                slugAvailable={slugAvailable}
                                validatingSlug={validatingSlug}
                                creating={creating}
                                fileInputRef={fileInputRef}
                                logoFile={logoFile}
                                motto={motto}
                                handleSchoolSelection={handleSchoolSelection}
                                handleNameChange={handleNameChange}
                                setSchoolName={setSchoolName}
                                setSubdomain={setSubdomain}
                                setSlugAvailable={setSlugAvailable}
                                checkAvailability={checkAvailability}
                                setLogoFile={setLogoFile}
                                setIsActive={setIsActive}
                                setMotto={setMotto}
                                setView={setView}
                                setEditingSchool={setEditingSchool}
                                handleCreate={handleCreate}
                                handleUpdate={handleUpdate}
                                // Contact
                                phones={phones} setPhones={setPhones}
                                email={email} setEmail={setEmail}
                                whatsapp={whatsapp} setWhatsapp={setWhatsapp}
                                address={address} setAddress={setAddress}
                                mapsLink={mapsLink} setMapsLink={setMapsLink}
                                fbLink={fbLink} setFbLink={setFbLink}
                                twitterLink={twitterLink} setTwitterLink={setTwitterLink}
                                instaLink={instaLink} setInstaLink={setInstaLink}
                                // Story sections
                                mission={mission} setMission={setMission}
                                vision={vision} setVision={setVision}
                                about={about} setAbout={setAbout}
                                nationalAnthem={nationalAnthem} setNationalAnthem={setNationalAnthem}
                                schoolAnthem={schoolAnthem} setSchoolAnthem={setSchoolAnthem}
                                whyChooseUs={whyChooseUs} setWhyChooseUs={setWhyChooseUs}
                                historyText={historyText} setHistoryText={setHistoryText}
                                coreValues={coreValues} setCoreValues={setCoreValues}
                                quote={quote} setQuote={setQuote}
                                quoteAuthor={quoteAuthor} setQuoteAuthor={setQuoteAuthor}
                                // Story files
                                missionFile={missionFile} setMissionFile={setMissionFile}
                                visionFile={visionFile} setVisionFile={setVisionFile}
                                aboutFile={aboutFile} setAboutFile={setAboutFile}
                                nationalAnthemFile={nationalAnthemFile} setNationalAnthemFile={setNationalAnthemFile}
                                schoolAnthemFile={schoolAnthemFile} setSchoolAnthemFile={setSchoolAnthemFile}
                                whyChooseUsFile={whyChooseUsFile} setWhyChooseUsFile={setWhyChooseUsFile}
                                // Header photos
                                headerPhotos={headerPhotos} setHeaderPhotos={setHeaderPhotos}
                                // Stats
                                totalStudents={totalStudents} setTotalStudents={setTotalStudents}
                                totalStaff={totalStaff} setTotalStaff={setTotalStaff}
                                yearFounded={yearFounded} setYearFounded={setYearFounded}
                                totalAlumni={totalAlumni} setTotalAlumni={setTotalAlumni}
                                // Principal
                                principalName={principalName} setPrincipalName={setPrincipalName}
                                principalPhoto={principalPhoto} setPrincipalPhoto={setPrincipalPhoto}
                                principalMessage={principalMessage} setPrincipalMessage={setPrincipalMessage}
                                // Curriculum
                                curriculumDesc={curriculumDesc} setCurriculumDesc={setCurriculumDesc}
                                compulsorySubjects={compulsorySubjects} setCompulsorySubjects={setCompulsorySubjects}
                                optionalSubjects={optionalSubjects} setOptionalSubjects={setOptionalSubjects}
                                // Arrays
                                staffMembers={staffMembers} setStaffMembers={setStaffMembers}
                                boardMembers={boardMembers} setBoardMembers={setBoardMembers}
                                activities={activities} setActivities={setActivities}
                                facilities={facilities} setFacilities={setFacilities}
                                facilityCategories={facilityCategories} setFacilityCategories={setFacilityCategories}
                                galleryItems={galleryItems} setGalleryItems={setGalleryItems}
                                galleryCategories={galleryCategories} setGalleryCategories={setGalleryCategories}
                                paymentMethods={paymentMethods} setPaymentMethods={setPaymentMethods}
                                feeBreakdown={feeBreakdown} setFeeBreakdown={setFeeBreakdown}
                                // Admissions
                                admissionProcess={admissionProcess} setAdmissionProcess={setAdmissionProcess}
                                admissionRequirements={admissionRequirements} setAdmissionRequirements={setAdmissionRequirements}
                                admissionFormFile={admissionFormFile} setAdmissionFormFile={setAdmissionFormFile}
                                toast={toast}
                            />
                        )}
                    </main>
                </div>
            </div>
        </SidebarProvider>
    );
};

export default OwnerSchoolWebsite;
